/**
 * 骑士档案页面
 *
 * 展示骑士的荣耀信息：
 * - 等级和徽章
 * - 统计数据
 * - 传奇作品
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { LegendCard, LegendCardSkeleton } from '../components/legend';
import { BadgeWall, AllBadges } from '../components/rider';
import * as legendApi from '../services/legendApi';
import styles from './RiderProfilePage.module.css';

export default function RiderProfilePage() {
    const { riderId } = useParams();
    const { t, language } = useLanguage();
    const { user, isLoggedIn } = useAuth();

    const [rider, setRider] = useState(null);
    const [legends, setLegends] = useState([]);
    const [loading, setLoading] = useState(true);
    const [legendsLoading, setLegendsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('legends'); // legends | badges
    const [isFollowing, setIsFollowing] = useState(false);
    const [followLoading, setFollowLoading] = useState(false);

    const isOwnProfile = isLoggedIn && user?.id === riderId;

    useEffect(() => {
        loadRiderProfile();
        loadRiderLegends();
    }, [riderId]);

    const loadRiderProfile = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await legendApi.getRiderProfile(riderId);
            if (result.success) {
                setRider(result.data);
                setIsFollowing(result.data.is_following || false);
            } else {
                setError(result.error || 'Failed to load rider profile');
            }
        } catch (err) {
            console.error('Failed to load rider profile:', err);
            setError('Failed to load rider profile');
        } finally {
            setLoading(false);
        }
    };

    const loadRiderLegends = async () => {
        setLegendsLoading(true);
        try {
            const result = await legendApi.getRiderLegends(riderId);
            if (result.success) {
                setLegends(result.data || []);
            }
        } catch (err) {
            console.error('Failed to load rider legends:', err);
        } finally {
            setLegendsLoading(false);
        }
    };

    const handleFollow = async () => {
        if (!isLoggedIn) return;

        setFollowLoading(true);
        try {
            if (isFollowing) {
                await legendApi.unfollowRider(riderId);
                setIsFollowing(false);
                setRider(prev => ({
                    ...prev,
                    followers_count: Math.max(0, (prev.followers_count || 0) - 1)
                }));
            } else {
                await legendApi.followRider(riderId);
                setIsFollowing(true);
                setRider(prev => ({
                    ...prev,
                    followers_count: (prev.followers_count || 0) + 1
                }));
            }
        } catch (err) {
            console.error('Follow action failed:', err);
        } finally {
            setFollowLoading(false);
        }
    };

    const getLevelInfo = () => {
        if (!rider) return null;
        return legendApi.getRiderLevelInfo(rider.level || 1, language);
    };

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>Loading rider profile...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <div className={styles.error}>
                    <span className={styles.errorIcon}>!</span>
                    <h2>无法加载骑士档案</h2>
                    <p>{error}</p>
                    <Link to="/hall-of-fame" className={styles.backBtn}>
                        返回传奇殿堂
                    </Link>
                </div>
            </div>
        );
    }

    const levelInfo = getLevelInfo();

    return (
        <div className={styles.page}>
            {/* 骑士头部 */}
            <header className={styles.header}>
                <div className={styles.headerBg}></div>
                <div className={styles.headerContent}>
                    {/* 头像区域 */}
                    <div className={styles.avatarSection}>
                        <div className={styles.avatar}>
                            {rider.avatar_url ? (
                                <img src={rider.avatar_url} alt={rider.nickname} />
                            ) : (
                                <span className={styles.avatarFallback}>
                                    {rider.nickname?.charAt(0)?.toUpperCase() || 'R'}
                                </span>
                            )}
                            <div className={styles.levelBadge}>
                                <span className={styles.levelIcon}>{levelInfo?.icon}</span>
                            </div>
                        </div>
                    </div>

                    {/* 信息区域 */}
                    <div className={styles.infoSection}>
                        <div className={styles.nameRow}>
                            <h1 className={styles.nickname}>{rider.nickname}</h1>
                            {rider.is_verified && (
                                <span className={styles.verifiedBadge} title="官方认证">

                                </span>
                            )}
                        </div>

                        <div className={styles.levelRow}>
                            <span className={styles.levelTitle}>
                                Lv.{rider.level || 1} {levelInfo?.title}
                            </span>
                            <BadgeWall
                                badges={rider.badges || []}
                                maxDisplay={5}
                                size="small"
                            />
                        </div>

                        {rider.bio && (
                            <p className={styles.bio}>{rider.bio}</p>
                        )}

                        {rider.signature && (
                            <p className={styles.signature}>"{rider.signature}"</p>
                        )}
                    </div>

                    {/* 操作按钮 */}
                    <div className={styles.actions}>
                        {isOwnProfile ? (
                            <Link to="/profile/edit" className={styles.editBtn}>
                                编辑档案
                            </Link>
                        ) : (
                            <button
                                className={`${styles.followBtn} ${isFollowing ? styles.following : ''}`}
                                onClick={handleFollow}
                                disabled={followLoading || !isLoggedIn}
                            >
                                {followLoading ? '...' : isFollowing ? '已关注' : '+ 关注'}
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {/* 统计数据 */}
            <section className={styles.stats}>
                <div className={styles.statItem}>
                    <span className={styles.statValue}>
                        {legendApi.formatNumber(rider.legends_count || 0)}
                    </span>
                    <span className={styles.statLabel}>传奇作品</span>
                </div>
                <div className={styles.statDivider}></div>
                <div className={styles.statItem}>
                    <span className={styles.statValue}>
                        {legendApi.formatNumber(rider.total_salutes || 0)}
                    </span>
                    <span className={styles.statLabel}>获得致敬</span>
                </div>
                <div className={styles.statDivider}></div>
                <div className={styles.statItem}>
                    <span className={styles.statValue}>
                        {legendApi.formatNumber(rider.followers_count || 0)}
                    </span>
                    <span className={styles.statLabel}>关注者</span>
                </div>
                <div className={styles.statDivider}></div>
                <div className={styles.statItem}>
                    <span className={styles.statValue}>
                        {legendApi.formatNumber(rider.following_count || 0)}
                    </span>
                    <span className={styles.statLabel}>关注中</span>
                </div>
            </section>

            {/* 标签页 */}
            <div className={styles.tabs}>
                <button
                    className={`${styles.tab} ${activeTab === 'legends' ? styles.active : ''}`}
                    onClick={() => setActiveTab('legends')}
                >
                    传奇作品
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'badges' ? styles.active : ''}`}
                    onClick={() => setActiveTab('badges')}
                >
                    徽章收藏
                </button>
            </div>

            {/* 内容区域 */}
            <div className={styles.content}>
                {activeTab === 'legends' ? (
                    <div className={styles.legendsTab}>
                        {legendsLoading ? (
                            <div className={styles.legendsGrid}>
                                {[...Array(6)].map((_, i) => (
                                    <LegendCardSkeleton key={i} />
                                ))}
                            </div>
                        ) : legends.length === 0 ? (
                            <div className={styles.empty}>
                                <span className={styles.emptyIcon}></span>
                                <p>
                                    {isOwnProfile
                                        ? '你还没有铸造任何传奇作品'
                                        : '这位骑士还没有铸造传奇作品'}
                                </p>
                                {isOwnProfile && (
                                    <Link to="/" className={styles.emptyBtn}>
                                        开始铸造传奇
                                    </Link>
                                )}
                            </div>
                        ) : (
                            <div className={styles.legendsGrid}>
                                {legends.map(legend => (
                                    <LegendCard
                                        key={legend.id}
                                        legend={legend}
                                        showRider={false}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={styles.badgesTab}>
                        <AllBadges
                            earnedBadges={rider.badges || []}
                            showLocked={isOwnProfile}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
